import { useState } from 'react';

import { api, getUser } from '../api.js';
import Modal from './Modal.jsx';
import ScopeSelect from './ScopeSelect.jsx';

/**
 * Modal zum Anlegen einer dokumentierten Totara-Einstellung.
 *
 * Props:
 *  - mandanten: Mandantenliste fürs Geltungs-Dropdown
 *  - categories: Kategorie-Vorschläge (datalist, Freitext bleibt erlaubt)
 *  - courseSets: Kursset-Liste fürs Geltungs-Dropdown
 *  - defaultScope?: { mandant_id, course_set } als Vorbelegung
 *  - onClose: () => void
 *  - onSaved: () => Promise|void
 *
 * Die ausführliche Markdown-Beschreibung wird bewusst erst auf der
 * Detailseite gepflegt – das Modal hält das Anlegen schlank.
 */
export default function TotaraSettingFormModal({
    mandanten,
    categories,
    courseSets,
    defaultScope,
    onClose,
    onSaved,
}) {
    const [category, setCategory] = useState('Allgemein');
    const [name, setName] = useState('');
    const [value, setValue] = useState('');
    const [scope, setScope] = useState(
        defaultScope || { mandant_id: null, course_set: null }
    );
    const [author, setAuthor] = useState(getUser() || '');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    async function handleSubmit(e) {
        e.preventDefault();
        setSaving(true);
        setError('');
        try {
            await api.createTotaraSetting({
                category: category || 'Allgemein',
                name,
                value: value || null,
                mandant_id: scope.mandant_id,
                course_set: scope.course_set,
                author: author || null,
            });
            await onSaved();
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    }

    return (
        <Modal
            title="Neue Totara-Einstellung"
            onClose={onClose}
            footer={
                <>
                    <button type="button" className="btn" onClick={onClose}>
                        Abbrechen
                    </button>
                    <button
                        type="submit"
                        form="totara-setting-form"
                        className="btn btn--primary"
                        disabled={saving}
                    >
                        {saving ? 'Speichere…' : 'Speichern'}
                    </button>
                </>
            }
        >
            <form id="totara-setting-form" onSubmit={handleSubmit}>
                {error && <div className="login__error">{error}</div>}

                <div className="grid-2">
                    <div className="field">
                        <label>Kategorie</label>
                        <input
                            type="text"
                            list="totara-categories"
                            value={category}
                            onChange={(e) => setCategory(e.target.value)}
                            placeholder="z. B. Kurse"
                        />
                        <datalist id="totara-categories">
                            {(categories || []).map((c) => (
                                <option key={c} value={c} />
                            ))}
                        </datalist>
                    </div>
                    <div className="field">
                        <label>Geltung</label>
                        <ScopeSelect
                            value={scope}
                            onChange={setScope}
                            mandanten={mandanten}
                            courseSets={courseSets}
                        />
                    </div>
                </div>

                <div className="field">
                    <label>Bezeichnung *</label>
                    <input
                        type="text"
                        required
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="z. B. Standard-Kursformat"
                    />
                </div>

                <div className="field">
                    <label>Wert</label>
                    <input
                        type="text"
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                        placeholder="z. B. Themenformat"
                    />
                </div>

                <div className="field">
                    <label>Autor</label>
                    <input
                        type="text"
                        value={author}
                        onChange={(e) => setAuthor(e.target.value)}
                    />
                </div>

                <div className="muted" style={{ fontSize: 12 }}>
                    Die ausführliche Beschreibung (Markdown) wird nach dem Speichern
                    auf der Detailseite gepflegt.
                </div>
            </form>
        </Modal>
    );
}
